import { useState } from "react";
import { contentLibrary } from "../../../mocks/platformData";

const filters = ["전체", "TikTok", "YouTube Shorts", "Instagram Reels"];
const statusColors: Record<string, string> = { published: "#10B981", archived: "#6B7280", processing: "#F59E0B" };
const statusLabels: Record<string, string> = { published: "게시됨", archived: "보관됨", processing: "처리 중" };

export default function ContentLibrarySection() {
  const [activeFilter, setActiveFilter] = useState("전체");

  const filtered = activeFilter === "전체"
    ? contentLibrary
    : contentLibrary.filter((c) => c.platform === activeFilter);

  return (
    <section className="py-28 bg-[#0A0E14] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-[#F59E0B]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-[#F59E0B] text-xs font-semibold uppercase tracking-widest mb-3 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              07 / 콘텐츠 라이브러리 및 자산 관리
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              내 콘텐츠, <span className="text-[#F59E0B]">체계적으로</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === f ? "bg-[#F59E0B] text-black" : "bg-[#1A1F2E] text-gray-400 hover:text-white border border-white/8"
                }`}
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "총 클립 수", value: "2,847", icon: "ri-film-line", color: "#00D9FF" },
            { label: "총 조회 수", value: "428M", icon: "ri-eye-line", color: "#10B981" },
            { label: "평균 참여율", value: "9.8%", icon: "ri-heart-3-line", color: "#F59E0B" },
            { label: "사용 스토리지", value: "3.4 TB", icon: "ri-hard-drive-3-line", color: "#00D9FF" },
          ].map((s) => (
            <div key={s.label} className="bg-[#0F1419] border border-white/8 rounded-xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${s.color}15` }}>
                <i className={`${s.icon} text-base`} style={{ color: s.color }} />
              </div>
              <div>
                <div className="text-white font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace", color: s.color }}>{s.value}</div>
                <div className="text-gray-500 text-xs">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((clip) => (
            <div key={clip.id} className="bg-[#0F1419] border border-white/8 rounded-2xl overflow-hidden group hover:border-[#F59E0B]/25 transition-all duration-300 cursor-pointer">
              <div className="relative overflow-hidden" style={{ aspectRatio: "16/9" }}>
                <img src={clip.thumbnail} alt={clip.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="text-xs px-2 py-1 rounded-full" style={{ color: statusColors[clip.status], backgroundColor: `${statusColors[clip.status]}15`, border: `1px solid ${statusColors[clip.status]}30` }}>
                    {statusLabels[clip.status]}
                  </span>
                </div>
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {clip.duration}
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex items-center gap-2">
                    {[{ icon: "ri-play-circle-line", label: "재생" }, { icon: "ri-edit-line", label: "편집" }, { icon: "ri-upload-2-line", label: "내보내기" }].map((btn) => (
                      <button key={btn.icon} className="w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-sm hover:bg-[#F59E0B]/80 text-white rounded-full transition-colors cursor-pointer" title={btn.label}>
                        <i className={`${btn.icon} text-sm`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h4 className="text-white text-sm font-semibold mb-3 leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{clip.title}</h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs">{clip.platform}</span>
                    <span className="text-gray-700">·</span>
                    <span className="text-gray-500 text-xs">{clip.date}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    <span className="flex items-center gap-1 text-gray-400">
                      <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line text-xs" /></span>
                      {clip.views}
                    </span>
                    <span className="text-[#10B981]">{clip.engagement}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <button className="border border-white/15 hover:border-[#F59E0B]/40 text-gray-400 hover:text-white text-sm px-8 py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            더 많은 클립 보기
          </button>
        </div>
      </div>
    </section>
  );
}
